import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { createLoadingSelector, getMeetingsRequest, GET_MEETINGS, useAppSelector } from 'store'
import { EEmptyListIconSize, EmptyList } from 'App/components/common'
import { generateRandomArray } from 'utils'
import { ReactComponent as ScheduleIcon } from 'assets/icons/Schedule.svg'

import { MeetingGroup } from '../MeetingGroup'
import { OngoingMeetingCalls } from '../OngoingMeetingCalls'

import { MeetingsModalScheduleButton } from './MeetingsModalScheduleButton'
import styles from './MeetingsModal.module.scss'

const loadingSelector = createLoadingSelector([GET_MEETINGS])

const DEFAULT_GROUPS_LENGTH = 3

type TMeetingsModalProps = { anchorId?: string }

export const MeetingsModal = ({ anchorId }: TMeetingsModalProps) => {
  const dispatch = useDispatch()

  const loading = useAppSelector(loadingSelector)
  const meetingsGroupIds = useAppSelector((state) => state.meetings.groupIds)
  const ongoingCallsIds = useAppSelector((state) => state.calls.ongoingCalls.ids)
  const activeCallId = useAppSelector((state) => state.calls.answer.createCall?.id)
  const loadedCount = useAppSelector((state) => Object.keys(state.meetings.list).length)
  const total = useAppSelector((state) => state.meetings.total)

  const { t } = useTranslation()

  const ids = loading ? generateRandomArray(DEFAULT_GROUPS_LENGTH) : meetingsGroupIds
  const shouldShowPlaceholder = !loading && !meetingsGroupIds.length && !ongoingCallsIds.length

  useEffect(() => {
    dispatch(getMeetingsRequest())
  }, [dispatch, activeCallId])

  const pageRef = useRef(0)

  useEffect(() => {
    pageRef.current = 0
  }, [activeCallId])

  const sentinelRef = useRef<HTMLDivElement>(null)

  const hasMore = loadedCount < total

  useEffect(() => {
    const sentinel = sentinelRef.current

    if (!sentinel || !hasMore || loading) {
      return
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return
      }

      pageRef.current += 1
      dispatch(getMeetingsRequest({ page: pageRef.current }))
    })

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [dispatch, hasMore, loading])

  if (shouldShowPlaceholder) {
    return (
      <>
        <MeetingsModalScheduleButton className={styles.scheduleButtonMargin} />

        <EmptyList
          text={t('modal.meetings.placeholder')}
          icon={<ScheduleIcon />}
          iconSize={EEmptyListIconSize.MD}
        />
      </>
    )
  }

  return (
    <>
      <MeetingsModalScheduleButton className={styles.scheduleButtonMargin} />

      {!loading && <OngoingMeetingCalls />}
      {ids.map((day) => (
        <MeetingGroup key={day} id={day} loading={loading} anchorId={anchorId} />
      ))}

      <div ref={sentinelRef} />
    </>
  )
}
